import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';

import HelperNavigation from '../../common/HelperNavigation';
import Paths from '../../../types/Paths';
import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import MyAccountBasicInfo from '../MyAccountBasicInfo';
import RouteService from '../../../services/RouteService';
import ContentForState from '../../loader/ContentForState';
import MainOrderList from '../../order/MainOrderList';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import ConfigStore from '../../../store/ConfigStore';
import RequestState from '../../../types/RequestState';

const ORDER_PREVIEW_COUNT = 3;

@observer
export class MyAccountOverview extends Component {
  constructor(props) {
    super(props);

    this.state = {
      orderCount: 0,
    };
  }

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount,
      },
    ]);
  };

  componentDidMount() {
    this.maybeLoadAccountInfo();
  }

  maybeLoadAccountInfo = () => {
    const { accountStore } = this.props;

    if (accountStore.state === RequestState.NONE) {
      accountStore.loadAccount().catch((err) => {
        console.error(err);
      });
    }
  };

  getName = () => {
    const { accountStore } = this.props;
    const { firstname, lastname } = accountStore.account.personal_info;

    if (firstname && lastname) {
      return firstname + ' ' + lastname;
    }
    return lastname ? lastname : firstname;
  };

  getAccountInfo = () => {
    const { accountStore, configStore } = this.props;

    const name = this.getName();

    const accountInfo = {
      customerId: accountStore.customerId,
      name: name,
      email: accountStore.account.personal_info.email,
      telephone: accountStore.account.personal_info.telephone,
      company: accountStore.account.personal_info.company,
    };

    if (configStore.account.showCustomerGroupName) {
      accountInfo.group = accountStore.account.group_name;
    }

    return accountInfo;
  };

  updateOrderCount = (orderCount) => {
    this.setState({ orderCount });
  };

  getCustomerGroupName = () => {
    const { accountStore, configStore } = this.props;

    return (
      configStore.account.showCustomerGroupName && (
        <span className="MyAccountOverview__customer-group-name">
          ({accountStore.account.group_name})
        </span>
      )
    );
  };

  render() {
    const { accountStore, routeService } = this.props;

    if (!accountStore.account || !accountStore.account.is_logged) {
      return null;
    }

    return (
      <>
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <div className="MyAccountOverview">
          <SEOTitle title={globalTranslations.myAccount} />
          <PageTitle>
            <FormattedMessage {...globalTranslations.myAccount} />
            {this.getCustomerGroupName()}
          </PageTitle>
          <ContentForState
            state={accountStore.state}
            error={accountStore.lastError}
            forLoaded={() => {
              return (
                <>
                  <MyAccountBasicInfo myAccountInfo={this.getAccountInfo()} />
                  {!accountStore.isBalanceBuyer && (
                    <>
                      <h2>
                        <FormattedMessage
                          id="myAccount.previousOrders"
                          defaultMessage="Previous {orderPreviewCount} orders"
                          values={{
                            orderPreviewCount: ORDER_PREVIEW_COUNT,
                          }}
                        />
                      </h2>
                      <MainOrderList
                        resultsPerPage={ORDER_PREVIEW_COUNT}
                        singlePage
                        onOrderCount={this.updateOrderCount}
                        showHeading={false}
                      />
                      {this.state.orderCount > ORDER_PREVIEW_COUNT && (
                        <Link
                          to={routeService.getPath(Paths.MyAccountOrderList)}
                        >
                          <Button
                            color="primary"
                            className="MyAccountOverview__my-account-order-history-link-button"
                          >
                            <span>
                              <FormattedMessage
                                {...globalTranslations.myOrderHistoryTitle}
                              />
                              {' - '}
                              <FormattedMessage
                                id="myAccount.orderHistory"
                                defaultMessage="See all {count, plural, one {# order} other {# orders}}"
                                values={{
                                  count: this.state.orderCount,
                                }}
                              />
                            </span>
                          </Button>
                        </Link>
                      )}
                    </>
                  )}
                </>
              );
            }}
          />
        </div>
      </>
    );
  }
}

MyAccountOverview.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  intl: intlShape.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
};

export default injectIntl(
  inject('accountStore', 'configStore', 'routeService')(MyAccountOverview)
);
